import { Button, DatePickerDropdown, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { SubwalletEndpoint } from '@api/endpoints';
import { formatLocalDate } from '@shared/utilities';
import { CircularProgress, Divider, Stack, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface SubwalletProgramYearListProps {
  subwalletId: string,
}

const toDate = (dateOnly: string | null) => (
  dateOnly ? new Date(`${dateOnly}T00:00:00`) : undefined
);

export const SubwalletProgramYearList = ({ subwalletId }: SubwalletProgramYearListProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { data: programYears, error, isFetching } = useQuery(
    [QueryKeys.LIST_SUBWALLET_PROGRAM_YEARS, subwalletId],
    () => SubwalletApi.listProgramYears(subwalletId),
  );

  const { mutate: setLock } = useMutation(
    ({ lockedUntilDate, rewardsProgramId }: {
      lockedUntilDate: string | null,
      rewardsProgramId: string,
    }) => SubwalletApi.updateProgramYearLock(subwalletId, rewardsProgramId, { lockedUntilDate }),
    {
      // Without this the picker just snaps back to its old value - the query that drives it is
      // only invalidated on success - so a rejected lock date would look like nothing happened.
      onError: (error) => openSnackbar((error as Error).message),
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          [QueryKeys.LIST_SUBWALLET_PROGRAM_YEARS, subwalletId],
        );
        await queryClient.invalidateQueries([QueryKeys.LIST_SUBWALLETS]);
      },
    },
  );

  const handleLockDateChanged = (
    programYear: SubwalletEndpoint.ListProgramYears.ProgramYear,
    value?: Date,
  ) => {
    setLock({
      lockedUntilDate: value ? formatLocalDate(value) : null,
      rewardsProgramId: programYear.rewardsProgramId,
    });
  };

  return (
    <Stack>
      <Text category="title-small" fontWeight={500}>
        {t($ => $.wallet.programYears.title)}
      </Text>
      <VSpacer size="2" />
      <Text category="body-small">
        {t($ => $.wallet.programYears.subtitle)}
      </Text>
      <VSpacer size="3" />
      {isFetching && <CircularProgress size={24} />}
      {!isFetching && !!error && (
        <Text category="body-medium" color="error">
          {t($ => $.wallet.programYears.loadFailed)}
        </Text>
      )}
      {!isFetching && !error && !programYears?.length && (
        <Text category="body-medium">
          {t($ => $.wallet.programYears.none)}
        </Text>
      )}
      <Stack divider={<Divider />} spacing={2}>
        {programYears?.map((programYear) => (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            key={programYear.rewardsProgramId}
            pt={2}
          >
            <Stack>
              <Text category="body-medium">
                {t($ => $.wallet.programYearName, {
                  name: programYear.name,
                  year: programYear.year,
                })}
              </Text>
              <Text category="body-small" sx={{ color: theme.palette.grey[700] }}>
                {t($ => $.wallet.programYears.pointsExpire, {
                  date: new Date(`${programYear.pointsExpirationDate}T00:00:00`),
                })}
              </Text>
            </Stack>
            <Stack alignItems="center" direction="row" spacing={2}>
              <DatePickerDropdown
                hideHelperText
                label={t($ => $.wallet.programYears.lockedUntilLabel)}
                // Saves on change, so incomplete or invalid text must not clear the lock.
                onChange={(value) => value && handleLockDateChanged(programYear, value)}
                onClear={() => (
                  programYear.lockedUntilDate && handleLockDateChanged(programYear, undefined)
                )}
                sendsTypedDateOnBlur
                testID={`subwallet-program-year-${programYear.rewardsProgramId}-lock-date`}
                value={toDate(programYear.lockedUntilDate)}
                width={220}
              />
              <Button
                disabled={!programYear.lockedUntilDate}
                onClick={() => handleLockDateChanged(programYear, undefined)}
                testID={`subwallet-program-year-${programYear.rewardsProgramId}-clear-lock`}
                variant="text"
              >
                {t($ => $.wallet.programYears.clearLock)}
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
